import React, { useEffect } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
  useParams,
} from 'react-router-dom';
import Nav from './components/common/Nav';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PrivateRoute from './components/common/PrivateRoute';
import OrganizationPage from './components/organizations/OrganizationPage';
import SourcesPage from './components/sources/SourcesPage';
import RoutesPage from './components/routes/RoutesPage';
import AccountPage from './components/account/AccountPage';
import { ManageOrganizationDetails } from './components/organizations/ManageOrganizationDetails';
import DestinationsPage from './components/destinations/DestinationsPage';
import { ManageRouteDetails } from './components/routes/ManageRouteDetails';
import { useKeycloakToken, LoginModal } from './components/common/useKeycloak';
import { scrollAnchorKey } from './components/common/scrollAnchor';
import { ManageDestinationDetails } from './components/destinations/ManageDestinationDetails';
import { ManageAddRouteStepOne } from './components/routes/ManageAddRouteStepOne';
import { ManageAddRouteStepTwo } from './components/routes/ManageAddRouteStepTwo';
import { ManageDestinationModal } from './components/destinations/ManageDestinationModal';
import { HelpProvider } from './context/HelpContext';
import HelpSidePanel from './components/common/HelpSidePanel';
import {
  trackPageView,
  setAnalyticsUserProperties,
  getPageTitle,
} from './analytics/analytics';
import { RequireContactEmailGate } from './auth/useRequireContactEmail';
import NotFoundPage from './components/common/NotFoundPage';
import { resetApiCallLog } from './api/apiUtils';
import { legacyDestinationsTarget } from './components/common/legacyPathRedirects';
import LegacyConnectionSubpageRedirect from './components/destinations/LegacyConnectionSubpageRedirect';
import { TutorialProvider } from './components/tutorial/TutorialContext'; // GUNDI-5665, temporary
import TutorialOverlay from './components/tutorial/TutorialOverlay'; // GUNDI-5665, temporary
import TutorialStartModal from './components/tutorial/TutorialStartModal'; // GUNDI-5665, temporary

// GUNDI-5642: the new portal's whole tree — its own module so that App.tsx can
// load it lazily. Legacy is the default tree now, so nobody downloads this
// chunk until the hidden unlock in the legacy nav switches them over.
// Puts every new screen at its top — see scrollAnchorKey for why the key is the
// section and the record rather than the whole path.
function ScrollToTopOnScreenChange() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const screen = scrollAnchorKey(pathname);
  // Not on Back or Forward: the browser restores the scroll position of the page
  // being returned to, and overriding that dropped the user at the top of a list
  // they had scrolled far down before opening a record.
  const isHistoryNavigation = navigationType === 'POP';
  useEffect(() => {
    if (isHistoryNavigation) return;
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);
  return null;
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    // Developer-only API-call counter (GUNDI-5464): start a fresh cumulative
    // tally for each view. No-op output unless the debug flag is set.
    resetApiCallLog();
    trackPageView(location.pathname, getPageTitle(location.pathname));
  }, [location.pathname]);
  return null;
}

// A record can be a destination in one Route and the source of another, so the
// section that lists them is "Connections". The old /destinations and
// /destination URLs are in bookmarks and support docs — keep them working.
const LegacyDestinationsRedirect = () => {
  const { pathname, search } = useLocation();
  return <Navigate to={legacyDestinationsTarget(pathname, search)} replace />;
};

// Several routes render the same details screen (/routes/:id and its tabs), so
// React Router hands the mounted screen a new :id instead of building a fresh
// one. Its data is fetched when it mounts, so
// without this it would keep showing the previous Route under the new URL
// until a reload. Keying on the id makes a different Route a new screen,
// while moving between tabs of the same Route still keeps its state.
const RouteDetailsRoute = () => {
  const { route_id } = useParams();
  return <ManageRouteDetails key={route_id} />;
};

// Links minted while the flow map's selection lived in the URL
// (/routes/:id/configurations/account/:id and the flow/… variants). The
// selection is component state now, so the tail names nothing this app can
// restore — keep the Route reachable and drop the rest, rather than 404 a
// bookmark over a detail the page will pick for itself.
export const RouteSelectionLinkRedirect = () => {
  const { route_id } = useParams();
  // Keep the query string: LegacyConnectionSubpageRedirect maps old tab links to
  // /routes/:id/configurations WITH their search, and that URL lands here — so
  // dropping it would lose a filter that the /logs and /sources spellings keep.
  const { search } = useLocation();
  return <Navigate to={`/routes/${route_id}${search}`} replace />;
};

// /connections/:id/configuration names the tab the bare URL already shows, so
// it folds into it rather than giving one tab two addresses.
export const ConnectionConfigurationRedirect = () => {
  const { dest_id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/connections/${dest_id}${search}`} replace />;
};

// Same for the Connection screen, reached both on its own and from inside a
// Route.
const DestinationDetailsRoute = () => {
  const { dest_id } = useParams();
  return (
    <div>
      <ManageDestinationDetails key={dest_id} />
    </div>
  );
};

export default function NewApp() {
  // overscroll-behavior only takes effect on the element that propagates to the
  // viewport, so the one new-tree rule that cannot be scoped under `.new-ui`
  // (see src/index.css) is keyed off a class on <html> instead. Added while
  // this tree is mounted and removed on unmount, so the frozen legacy tree
  // keeps its original scrolling.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('new-ui-root');
    return () => root.classList.remove('new-ui-root');
  }, []);

  return (
    <div className="new-ui">
      {/* GUNDI-5665 (TEMPORARY): the guided tour. Wraps the tree so the
              banner's "Take a tour" link — rendered by Nav below — sees the
              provider. See the removal manifest in tutorialInterceptor.ts. */}
      <TutorialProvider>
        <HelpProvider>
          <PageViewTracker />
          <ScrollToTopOnScreenChange />
          <RequireContactEmailGate />
          <Nav />
          <HelpSidePanel />
          {/* Clears the fixed chrome plus 24px of breathing room: an 80px
                  bar below md (pt-[104px]), and at md+ a 70px top bar with the
                  110px rail hanging beneath it (pt-[94px], pushed right by the
                  rail). The 24px is what the list sections' white title band
                  cancels with -mt-6 to sit flush against the bar.
                  The environment stripe on dev/stage adds ~22px on mobile that
                  this does not account for — as it did not before either. */}
          <div className="px-4 py-6 pt-[104px] md:ml-[110px] md:px-8 md:pt-[94px] desktop:px-[60px]">
            <Routes>
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <RoutesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <PrivateRoute>
                    <AccountPage />
                  </PrivateRoute>
                }
              />

              {/* workspaces */}
              <Route
                path="/workspaces"
                element={
                  <PrivateRoute>
                    <OrganizationPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/workspaces/:org_id"
                element={
                  <PrivateRoute>
                    <ManageOrganizationDetails />
                  </PrivateRoute>
                }
              />

              {/* sources */}
              <Route
                path="/sources" // sources (list)
                element={
                  <PrivateRoute>
                    <SourcesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/sources/destination/:dest_id"
                element={
                  <PrivateRoute>
                    <DestinationDetailsRoute />
                  </PrivateRoute>
                }
              />
              <Route
                path="/sources/destination/:dest_id/configuration"
                element={
                  <PrivateRoute>
                    <DestinationDetailsRoute />
                  </PrivateRoute>
                }
              />

              {/* Connections — every integration record, whatever role it
                      plays in a route. /destinations* redirects here. */}
              <Route
                path="/connections"
                element={
                  <PrivateRoute>
                    <DestinationsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/connections/new"
                element={
                  <PrivateRoute>
                    <ManageDestinationModal
                      onToggleModal={() => window.history.back()}
                    />
                  </PrivateRoute>
                }
              />
              {/* The Route wizard used to live under /connections/new; those deep
                      links land on a blank Route wizard — wizard state is not
                      restored from the URL. */}
              <Route
                path="/connections/new/configureprovider/*"
                element={<Navigate to="/routes/new" replace />}
              />
              <Route
                path="/connections/new/:connection_id/configuredestinations/*"
                element={<Navigate to="/routes/new" replace />}
              />
              <Route
                path="/connections/:dest_id"
                element={
                  <PrivateRoute>
                    <DestinationDetailsRoute />
                  </PrivateRoute>
                }
              />
              <Route
                path="/connections/:dest_id/routes"
                element={
                  <PrivateRoute>
                    <DestinationDetailsRoute />
                  </PrivateRoute>
                }
              />
              {/* A Connection has two addresses: /connections/:id (its
                      Configuration) and /connections/:id/routes. The bare path
                      already IS Configuration, so this spelling stays valid for
                      links but never stays in the address bar. The same screen
                      keeps its /configuration suffix at its other mount points
                      (/sources/destination/:id, /routes/:rid/:subpage/:id),
                      where the bare path means something else. */}
              <Route
                path="/connections/:dest_id/configuration"
                element={
                  <PrivateRoute>
                    <ConnectionConfigurationRedirect />
                  </PrivateRoute>
                }
              />
              {/* Old connection-details tab links (/connections/:id/logs etc.)
                      — a "connection" used to be today's route, so these resolve
                      the provider's route and land on the matching tab. The
                      static /routes and /configuration entries above outrank
                      this param match, so the new-vocabulary pages are safe. */}
              <Route
                path="/connections/:dest_id/:subpage"
                element={
                  <PrivateRoute>
                    <LegacyConnectionSubpageRedirect />
                  </PrivateRoute>
                }
              />
              {/* /connected-systems was this screen's URL on main, so links
                      minted then (tickets, Slack, GA landing pages) still resolve. */}
              <Route
                path="/connected-systems"
                element={<LegacyDestinationsRedirect />}
              />
              <Route
                path="/connected-systems/*"
                element={<LegacyDestinationsRedirect />}
              />
              <Route
                path="/destinations"
                element={<LegacyDestinationsRedirect />}
              />
              <Route
                path="/destinations/*"
                element={<LegacyDestinationsRedirect />}
              />
              <Route
                path="/destination/*"
                element={<LegacyDestinationsRedirect />}
              />
              <Route
                path="/routes/:route_id"
                element={
                  <PrivateRoute>
                    <RouteDetailsRoute />
                  </PrivateRoute>
                }
              />
              <Route
                path="/routes/:route_id/:subpage"
                element={
                  <PrivateRoute>
                    <RouteDetailsRoute />
                  </PrivateRoute>
                }
              />
              {/* The flow map's selection used to live in the URL
                      (/configurations/account/:id[/:section] and the flow/… variants).
                      It is component state now, so those links carry nothing this app
                      can restore — drop the tail and land on the Route itself rather
                      than 404 a bookmark. ManageRouteDetails turns the bare
                      /configurations into /routes/:route_id in the same way. */}
              <Route
                path="/routes/:route_id/configurations/*"
                element={
                  <PrivateRoute>
                    <RouteSelectionLinkRedirect />
                  </PrivateRoute>
                }
              />
              <Route
                path="/routes/:route_id/:subpage/newdestination"
                element={
                  <PrivateRoute>
                    <ManageDestinationModal
                      onToggleModal={() => window.history.back()}
                    />
                  </PrivateRoute>
                }
              />
              <Route
                path="/routes/:route_id/:subpage/:dest_id"
                element={
                  <PrivateRoute>
                    <DestinationDetailsRoute />
                  </PrivateRoute>
                }
              />
              <Route
                path="/routes/:route_id/:subpage/:dest_id/configuration"
                element={
                  <PrivateRoute>
                    <DestinationDetailsRoute />
                  </PrivateRoute>
                }
              />

              {/* Add Route flow  */}
              <Route
                path="/routes/new"
                element={
                  <PrivateRoute>
                    <ManageAddRouteStepOne />
                  </PrivateRoute>
                }
              />
              <Route
                path="/routes/new/configureprovider/:pt_value"
                element={
                  <PrivateRoute>
                    <ManageAddRouteStepTwo />
                  </PrivateRoute>
                }
              />
              {/* Catch-all 404 */}
              <Route
                path="*"
                element={
                  <PrivateRoute>
                    <NotFoundPage />
                  </PrivateRoute>
                }
              />
            </Routes>
            <ToastContainer />
            <Content />
          </div>
        </HelpProvider>
        <TutorialStartModal />
        <TutorialOverlay />
      </TutorialProvider>
    </div>
  );
}

function Content() {
  const [token, showLoginModal, handleLogin, isSessionExpired] =
    useKeycloakToken();

  useEffect(() => {
    if (token && typeof token === 'string') {
      setAnalyticsUserProperties(token);
    }
  }, [token]);

  return (
    <>
      {showLoginModal === true && (
        <LoginModal
          showLoginModal={showLoginModal}
          handleLogin={handleLogin}
          isSessionExpired={isSessionExpired}
        />
      )}
    </>
  );
}
